import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalSearchItemDto, GlobalSearchRecentActivityDto } from 'api/search';
import {
  buildProcessRoute,
  BROKER_PROCESS_PREFIX
} from 'back-office/features/group-contract-process/utils/getRouteByStep';
import { GroupContractStepIdentifier } from 'back-office/features/group-contract-process/types/enums';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

/**
 * Navigation au clic sur un résultat de recherche globale — par URL uniquement :
 *   riskId présent -> /simulation/dossier/{riskId} (la route résout l'étape et redirige) ;
 *   sinon personId -> fiche client ; sinon id selon le type (contratGroupe -> détail).
 * L'ouverture d'un dossier reste soumise à la matrice des droits — pour un rôle sans
 * accès process (ex. Plateforme), on retombe sur la fiche client.
 */
export const useGlobalSearchNavigation = (onNavigate?: () => void) => {
  const navigate = useNavigate();
  const canOpenQuote = useRoleCan(Capability.QuoteOpen);
  const canOpenSimulation = useRoleCan(Capability.SimulationOpen);

  const go = useCallback(
    (route: string) => {
      onNavigate?.();
      navigate(route);
    },
    [navigate, onNavigate]
  );

  const goToClient = useCallback(
    (personId?: string | null): boolean => {
      if (!personId) return false;
      go(`/mes-clients/${personId}`);
      return true;
    },
    [go]
  );

  const navigateToItem = useCallback(
    (item: GlobalSearchItemDto) => {
      switch (item.type) {
        case 'client':
          goToClient(item.personId ?? item.id);
          return;

        case 'contrat':
          go(`/mes-contrats/details/${item.id}`);
          return;

        case 'devis':
          if (canOpenQuote && item.riskId) {
            // Devis emprunteur : URL de contractualisation borrower (projet + risque + devis)
            go(
              item.borrowerProjectId
                ? `/simulation/contractualisation/borrower/${item.borrowerProjectId}/${item.riskId}/${item.id}`
                : `/simulation/contractualisation/${item.riskId}/${item.id}`
            );
            return;
          }
          goToClient(item.personId);
          return;

        case 'note':
          // Une note s'ouvre... sur la note : page Mes notes, note présélectionnée.
          go(`/mes-notes?noteId=${item.id}`);
          return;

        case 'simulation':
          if (canOpenSimulation && (item.riskId || item.borrowerProjectId)) {
            // Dossier emprunteur : résolution par projet (+ risque), sinon par risque
            go(
              item.borrowerProjectId
                ? `/simulation/dossier-emprunteur/${item.borrowerProjectId}${item.riskId ? `/${item.riskId}` : ''}`
                : `/simulation/dossier/${item.riskId}`
            );
            return;
          }
          goToClient(item.personId);
          return;

        case 'contratGroupe':
          go(
            buildProcessRoute(
              item.id,
              GroupContractStepIdentifier.Contractualization,
              BROKER_PROCESS_PREFIX
            )
          );
          return;

        default:
          goToClient(item.personId);
      }
    },
    [go, goToClient, canOpenQuote, canOpenSimulation]
  );

  /** Sous-lignes « dernières simulations » d'un client (id = risqueAssureId). */
  const navigateToActivity = useCallback(
    (activity: GlobalSearchRecentActivityDto) => {
      if (!canOpenSimulation) return;
      go(
        activity.borrowerProjectId
          ? `/simulation/dossier-emprunteur/${activity.borrowerProjectId}/${activity.id}`
          : `/simulation/dossier/${activity.id}`
      );
    },
    [canOpenSimulation, go]
  );

  return { navigateToItem, navigateToActivity, canOpenSimulation };
};
